import React, { useState } from "react";
import PropTypes from "prop-types";
import { Copy, Check, AlertTriangle, ChevronRight } from "lucide-react";

export function hasToolArguments(toolArgs) {
  if (toolArgs === null || toolArgs === undefined) return false;
  let parsed = toolArgs;
  if (typeof toolArgs === "string") {
    const trimmed = toolArgs.trim();
    if (!trimmed || trimmed === "{}" || trimmed === "[]" || trimmed === "null") {
      return false;
    }
    try {
      parsed = JSON.parse(trimmed);
    } catch (e) {
      parsed = trimmed;
    }
  }
  if (parsed === null || parsed === undefined) return false;
  if (Array.isArray(parsed)) {
    return parsed.length > 0;
  }
  if (typeof parsed === "object") {
    return Object.keys(parsed).length > 0;
  }
  return String(parsed).trim().length > 0;
}

export const StepInspector = ({ step, t, id }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!step || typeof step !== "object") return null;

  let parsedArgs = step.tool_args;
  if (typeof step.tool_args === "string") {
    try {
      parsedArgs = JSON.parse(step.tool_args);
    } catch (e) {
      parsedArgs = step.tool_args;
    }
  }

  const handleCopyArgs = async () => {
    const textToCopy =
      typeof parsedArgs === "object"
        ? JSON.stringify(parsedArgs, null, 2)
        : String(parsedArgs || "");
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = textToCopy;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn("Failed to copy parameters:", err);
    }
  };

  const hasArgs = hasToolArguments(step.tool_args);

  const previewOutput = step.tool_output_preview || step.tool_output;

  const durationStr =
    typeof step.execution_time_ms === "number"
      ? `${step.execution_time_ms}ms`
      : typeof step.duration_ms === "number"
      ? `${step.duration_ms}ms`
      : null;

  const hasDetails = Boolean(hasArgs || previewOutput);

  return (
    <div
      className="elelem-assistant-step-details"
      id={id}
      data-testid="copilot-step-inspector"
    >
      {step.status === "error" && step.error_message && (
        <div className="elelem-assistant-step-error text-danger d-flex align-items-center mb-2">
          <AlertTriangle size={12} className="me-1 flex-shrink-0" aria-hidden="true" />
          <span>{step.error_message}</span>
        </div>
      )}

      <div className="elelem-activity-details-header d-flex align-items-center justify-content-between">
        {hasDetails && (
          <button
            type="button"
            className="elelem-activity-details-toggle btn btn-sm btn-link p-0 text-decoration-none"
            onClick={() => setShowDetails((prev) => !prev)}
            data-testid="copilot-show-details-toggle"
            aria-expanded={showDetails}
          >
            <span>
              {showDetails
                ? t ? t("assistant.hideDetails", "Hide details") : "Hide details"
                : t ? t("assistant.showDetails", "Show details") : "Show details"}
            </span>
            <ChevronRight
              size={11}
              className={`elelem-activity-chevron ms-1 ${showDetails ? "open" : ""}`}
              aria-hidden="true"
            />
          </button>
        )}

        {durationStr && (
          <span className={`elelem-activity-meta-duration text-muted small ${!hasDetails ? "ms-auto" : ""}`}>
            {durationStr}
          </span>
        )}
      </div>

      {showDetails && hasDetails && (
        <div className="elelem-activity-nested-details mt-2" data-testid="copilot-nested-details">
          {hasArgs && (
            <div className="elelem-assistant-step-section mb-2">
              <div className="elelem-assistant-step-section-header d-flex justify-content-between align-items-center mb-1">
                <span>{t ? t("assistant.parameters", "Parameters") : "Parameters"}</span>
                <button
                  type="button"
                  className="elelem-assistant-copy-btn btn btn-sm btn-link p-0 text-decoration-none"
                  onClick={handleCopyArgs}
                  title={t ? t("assistant.copyParameters", "Copy parameters") : "Copy parameters"}
                  data-testid="copilot-copy-args-btn"
                >
                  {copied ? (
                    <>
                      <Check size={11} className="text-success me-1" aria-hidden="true" />
                      <span className="text-success">
                        {t ? t("assistant.copied", "Copied") : "Copied"}
                      </span>
                    </>
                  ) : (
                    <>
                      <Copy size={11} className="me-1" aria-hidden="true" />
                      <span>{t ? t("assistant.copy", "Copy") : "Copy"}</span>
                    </>
                  )}
                </button>
              </div>
              <pre className="elelem-assistant-code-snippet" data-testid="copilot-step-args">
                <code>
                  {typeof parsedArgs === "object"
                    ? JSON.stringify(parsedArgs, null, 2)
                    : String(parsedArgs)}
                </code>
              </pre>
            </div>
          )}

          {previewOutput && (
            <div className="elelem-assistant-step-section">
              <div className="elelem-assistant-step-section-header d-flex justify-content-between align-items-center mb-1">
                <span>{t ? t("assistant.outputPreview", "Output Preview") : "Output Preview"}</span>
                {step.tool_output_truncated && (
                  <span className="elelem-assistant-truncated-pill">
                    {t ? t("assistant.outputTruncated", "Truncated") : "Truncated"}
                  </span>
                )}
              </div>
              <pre className="elelem-assistant-code-snippet" data-testid="copilot-step-output">
                <code>{previewOutput}</code>
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

StepInspector.propTypes = {
  step: PropTypes.object.isRequired,
  t: PropTypes.func,
  id: PropTypes.string,
};
